import { motion, useReducedMotion } from 'motion/react';
import { durations, eases } from '@/shared/constants/motion.constants';

import { useStaticMotion } from '../StaticMotionContext';
import type { PageTransitionProps } from './PageTransition.types';
import { PAGE_TRANSITION_VARIANTS } from './PageTransition.constants';

/**
 * Page-level entrance animation wrapper.
 * Respects prefers-reduced-motion accessibility setting.
 */
export const PageTransition = ({
  children,
  variant = 'slideUp',
  delay = 0,
  animate = true,
  style,
  className,
}: PageTransitionProps) => {
  const shouldReduceMotion = useReducedMotion();
  const staticMotion = useStaticMotion();

  // Static mode (PDF export) renders the same plain, full-opacity wrapper as the
  // reduced-motion path — no tween the headless renderer could capture mid-flight.
  if (!animate || shouldReduceMotion || staticMotion) {
    return (
      <div style={style} className={className}>
        {children}
      </div>
    );
  }

  const { initial, animate: settled } = PAGE_TRANSITION_VARIANTS[variant];

  return (
    <motion.div
      initial={initial}
      animate={settled}
      transition={{
        duration: durations.slow,
        ease: eases.quartOut,
        delay,
      }}
      style={style}
      className={className}
    >
      {children}
    </motion.div>
  );
};

PageTransition.displayName = 'PageTransition';
